import React, { useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDeviceTags } from 'src/module/device/action';
import { getDeviceTags, getLoadingDeviceTags } from 'src/module/device/selector';
import LogoSpinner from 'src/component/UI/LogoSpinner';

type Props = {
  value: string;
  disabled?: boolean;
  deviceType: 'commLoop' | 'circuit' | 'controller';
  placeholder?: string;
  linkedDeviceTag?: string | null;
  onChange: (value: string) => void;
  onDeviceSelect?: (deviceId: string) => void;
  onUnlink?: () => void;
};

export default function DeviceTagAutocompleteField ({
  value,
  disabled,
  deviceType,
  placeholder,
  linkedDeviceTag,
  onChange,
  onDeviceSelect,
  onUnlink,
}: Props) {
  const dispatch = useDispatch();
  const deviceTags = useSelector(getDeviceTags);
  const loadingDeviceTags = useSelector(getLoadingDeviceTags);
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState(value || '');

  useEffect(() => {
    dispatch(fetchDeviceTags(null, deviceType, null));
  }, [dispatch, deviceType]);

  useEffect(() => {
    if (!loadingDeviceTags) {
      const optionsArray = deviceTags.map((device) => ({
        label: device.get('tag'),
        value: device.get('id'),
      })).toArray();
      setOptions(optionsArray);
    }
  }, [loadingDeviceTags, deviceTags]);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const handleSelect = (_, option) => {
    if (option) {
      const newValue = option.label;
      setInputValue(newValue);
      onChange(newValue);
      if (onDeviceSelect) {
        onDeviceSelect(option.value);
      }
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
    if (linkedDeviceTag && newValue !== linkedDeviceTag && onUnlink) {
      onUnlink();
    }
  };

  const handleAutocompleteInputChange = (_, newInputValue) => {
    setInputValue(newInputValue);
    if (linkedDeviceTag && newInputValue !== linkedDeviceTag && newInputValue && onUnlink) {
      onUnlink();
    }
    if (newInputValue && newInputValue.length >= 3) {
      dispatch(fetchDeviceTags(newInputValue, deviceType, null));
    }
  };

  return (
    <Autocomplete
      fullWidth
      disabled={disabled}
      inputValue={inputValue}
      options={options}
      loading={loadingDeviceTags}
      freeSolo
      isOptionEqualToValue={(option, val) => option && val && option.value === val.value}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || '')}
      onChange={handleSelect}
      onInputChange={handleAutocompleteInputChange}
      renderInput={(params) => (
        <TextField
          {...params}
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loadingDeviceTags && <LogoSpinner size={24} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
